import { useNavigate } from 'react-router-dom';
import { shouldRouteToBroker } from '@zg/shared/property';
import type { Classifier } from '@zg/shared/property';
import { Button } from '../Button';
import { Select } from '../form/Select';
import { usePropertyClassifier } from '../../hooks/usePropertyClassifier';
import { US_STATES } from '../../lib/usStates';
import { CheckboxGroup, RadioGroup } from './FormControls';

const propertyTypeOptions = [
  { value: 'apartment', label: 'Apartment building(s)', description: 'Rental, multifamily' },
  { value: 'condo', label: 'Condominium association', description: 'Master policy for the association' },
  { value: 'student', label: 'Student housing', description: 'Serves college or graduate students' },
  { value: 'senior', label: 'Senior / assisted living', description: 'Any level of senior care' },
  { value: 'mixed-use', label: 'Mixed-use', description: 'Primarily habitational, some commercial' },
];

const portfolioScopeOptions = [
  { value: 'single', label: 'One building / location' },
  {
    value: 'multiple',
    label: 'Multiple buildings',
    description: 'Portfolio schedules route to a broker for v1',
  },
];

const ownershipOptions = [
  { value: 'owner-operator', label: 'Owner-operator' },
  { value: 'developer-holding', label: 'Developer, still holding' },
  { value: 'association', label: 'Condo / HOA association' },
  { value: 'other', label: 'Other' },
];

const coverageOptions = [
  { value: 'property', label: 'Property' },
  { value: 'gl', label: 'General liability' },
  { value: 'umbrella', label: 'Excess / umbrella' },
  { value: 'wc', label: 'Workers compensation' },
];

const featureOptions = [
  { value: 'pool', label: 'Swimming pool' },
  { value: 'spa', label: 'Spa or hot tub' },
  { value: 'playground', label: 'Playground' },
  { value: 'clubhouse', label: 'Clubhouse' },
  { value: 'daycare', label: 'Daycare on premises' },
  { value: 'dog-park', label: 'Dog park' },
  { value: 'commercial-tenants', label: 'Commercial tenants' },
  { value: 'subsidized', label: 'Subsidized units (HUD / Section 8 / etc.)' },
];

const employeeOptions = [
  { value: 'none', label: 'No W-2 employees' },
  { value: '1-10', label: '1 – 10' },
  { value: '11-50', label: '11 – 50' },
  { value: '50+', label: '50+' },
];

const tivOptions = [
  { value: '<5M', label: 'Under $5M' },
  { value: '5-25M', label: '$5M – $25M' },
  { value: '25-100M', label: '$25M – $100M' },
  {
    value: '100M+',
    label: '$100M+',
    description: 'Very large accounts route to a broker for v1',
  },
];

export function PropertyClassifierForm() {
  const navigate = useNavigate();
  const { draft, errors, update, validate } = usePropertyClassifier();

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    const result = validate();
    if (!result.ok) {
      // scroll to first error
      const first = Object.keys(result.errors)[0];
      if (first) {
        const el = document.querySelector(`[data-qid="${first}"]`);
        el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }
    if (shouldRouteToBroker(result.classifier)) {
      navigate('/property/contact-broker', { state: { classifier: result.classifier } });
      return;
    }
    navigate('/property/form');
  }

  return (
    <form
      onSubmit={onSubmit}
      className="flex flex-col gap-10 max-w-3xl"
      aria-label="Property Insurance Quote — classification"
    >
      <div data-qid="propertyType">
        <RadioGroup
          name="propertyType"
          label="Property type"
          options={propertyTypeOptions}
          value={draft.propertyType}
          onChange={(v) => update('propertyType', v as Classifier['propertyType'])}
          error={errors.propertyType}
        />
      </div>

      <div data-qid="portfolioScope">
        <RadioGroup
          name="portfolioScope"
          label="Scope"
          options={portfolioScopeOptions}
          value={draft.portfolioScope}
          onChange={(v) => update('portfolioScope', v as Classifier['portfolioScope'])}
          error={errors.portfolioScope}
        />
      </div>

      <div data-qid="ownership">
        <RadioGroup
          name="ownership"
          label="Who is applying?"
          options={ownershipOptions}
          value={draft.ownership}
          onChange={(v) => update('ownership', v as Classifier['ownership'])}
          error={errors.ownership}
          layout="grid-2"
        />
      </div>

      <div data-qid="state">
        <Select
          label="State where the building is located"
          placeholder="Select a state"
          options={US_STATES}
          value={draft.state ?? ''}
          onChange={(e) => update('state', e.target.value)}
          error={errors.state}
        />
      </div>

      <div data-qid="coverages">
        <CheckboxGroup
          label="Coverages requested"
          options={coverageOptions}
          values={draft.coverages ?? []}
          onChange={(v) => update('coverages', v as Classifier['coverages'])}
          error={errors.coverages}
          layout="grid-2"
        />
      </div>

      <div data-qid="features">
        <CheckboxGroup
          label="Occupancy features (check all that apply)"
          options={featureOptions}
          values={draft.features ?? []}
          onChange={(v) => update('features', v as Classifier['features'])}
          error={errors.features}
          layout="grid-2"
          hint="If you're unsure, check what you know; follow-up sections ask the details."
        />
      </div>

      <div data-qid="employees">
        <RadioGroup
          name="employees"
          label="W-2 employees"
          options={employeeOptions}
          value={draft.employees}
          onChange={(v) => update('employees', v as Classifier['employees'])}
          error={errors.employees}
          layout="grid-2"
        />
      </div>

      <div data-qid="tivBucket">
        <RadioGroup
          name="tivBucket"
          label="Approximate total insurable value"
          options={tivOptions}
          value={draft.tivBucket}
          onChange={(v) => update('tivBucket', v as Classifier['tivBucket'])}
          error={errors.tivBucket}
          layout="grid-2"
        />
      </div>

      <div className="flex flex-wrap gap-3 pt-2">
        <Button type="submit" variant="primary" size="md">
          Continue
        </Button>
        <Button as="link" to="/" variant="ghost" size="md">
          Cancel
        </Button>
      </div>
    </form>
  );
}
